import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { trackAnalyticsEvent } from '../services/analytics';

interface CompareItem {
  id: number;
  name: string;
}

interface Props {
  listingId: number;
  listingName: string;
}

const STORAGE_KEY = 'compare_listings';
const MAX_COMPARE = 4;

function getCompareList(): CompareItem[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function setCompareList(items: CompareItem[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  window.dispatchEvent(new Event('compare-updated'));
}

const CompareToggle: React.FC<Props> = ({ listingId, listingName }) => {
  const [isAdded, setIsAdded] = useState(false);
  const [count, setCount] = useState(0);

  const sync = () => {
    const list = getCompareList();
    setIsAdded(list.some(item => item.id === listingId));
    setCount(list.length);
  };

  useEffect(() => {
    sync();
    const handler = () => sync();
    window.addEventListener('compare-updated', handler);
    window.addEventListener('storage', handler);
    return () => {
      window.removeEventListener('compare-updated', handler);
      window.removeEventListener('storage', handler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listingId]);

  const toggle = () => {
    const list = getCompareList();
    if (isAdded) {
      setCompareList(list.filter(item => item.id !== listingId));
    } else {
      if (list.length >= MAX_COMPARE) return;
      setCompareList([...list, { id: listingId, name: listingName }]);
      trackAnalyticsEvent('compare_add', { selection_count: list.length + 1 });
    }
  };

  return (
    <>
      <button onClick={toggle} disabled={!isAdded && count >= MAX_COMPARE} style={{
        display: 'inline-flex', alignItems: 'center', gap: '4px',
        padding: '5px 12px', borderRadius: '16px', fontSize: '12px', fontWeight: 600,
        cursor: !isAdded && count >= MAX_COMPARE ? 'not-allowed' : 'pointer',
        border: isAdded ? '1px solid #c8e6c9' : '1px solid #e0e0e0',
        background: isAdded ? '#e8f5e9' : '#fff',
        color: isAdded ? '#2e7d32' : '#666',
        transition: 'all 0.2s',
      }}>
        {isAdded ? '✓ Added' : 'Compare'}
        {count > 0 && <span style={{ opacity: 0.7 }}>({count})</span>}
      </button>

      {/* Floating Compare Bar */}
      {count >= 2 && (
        <CompareBar />
      )}
    </>
  );
};

const CompareBar: React.FC = () => {
  const [items, setItems] = useState<CompareItem[]>([]);

  useEffect(() => {
    const sync = () => setItems(getCompareList());
    sync();
    window.addEventListener('compare-updated', sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener('compare-updated', sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  if (items.length < 2) return null;

  const ids = items.map(i => i.id).join(',');

  return (
    <div style={{
      position: 'fixed', bottom: '20px', left: '50%', transform: 'translateX(-50%)',
      background: '#1a237e', color: '#fff', padding: '12px 24px',
      borderRadius: '30px', boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
      display: 'flex', alignItems: 'center', gap: '16px', zIndex: 9999,
      fontSize: '14px', fontWeight: 600,
    }}>
      <span>⚖️ {items.length} listings selected</span>
      <Link to={`/compare?ids=${ids}`} onClick={() => trackAnalyticsEvent('compare_start', { selection_count: items.length })} style={{
        padding: '6px 16px', background: '#fff', color: '#1a237e',
        borderRadius: '20px', textDecoration: 'none', fontWeight: 700,
        fontSize: '13px',
      }}>
        Compare Now
      </Link>
      <button onClick={() => setCompareList([])} style={{
        background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff',
        borderRadius: '50%', width: '24px', height: '24px', fontSize: '12px',
        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>✕</button>
    </div>
  );
};

export default CompareToggle;
